import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import { DataResult } from '../../utils/DataResult';

const AUTH_GATEWAY_ROUTE = 'developer-compass';

export interface ApiServiceOptions {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
}

interface ErrorEnvelope {
  errors?: unknown;
}

export function toMessages(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map(entry => {
      if (typeof entry === 'string') {
        return entry;
      }

      const message = (entry as { message?: unknown } | null)?.message;

      return typeof message === 'string' ? message : '';
    })
    .filter(message => message.length > 0);
}

export async function readJsonSafely(response: Response): Promise<unknown> {
  try {
    return await response.clone().json();
  } catch {
    return null;
  }
}

export async function toRequestError(response: Response): Promise<Error> {
  const body = (await readJsonSafely(response)) as ErrorEnvelope | null;
  const messages = toMessages(body?.errors);

  if (messages.length === 0) {
    return ResponseError.fromResponse(response);
  }

  return new Error(messages.join(' '));
}

/**
 * Shared base class for the Developer Compass API services. Centralizes
 * access to the auth-gateway base URL and the common request/response
 * handling used across every service.
 */
export abstract class BaseApiService {
  protected readonly discoveryApi: DiscoveryApi;
  protected readonly fetchApi: FetchApi;

  constructor({ discoveryApi, fetchApi }: ApiServiceOptions) {
    this.discoveryApi = discoveryApi;
    this.fetchApi = fetchApi;
  }

  protected async getBaseUrl(): Promise<string> {
    const gatewayUrl = await this.discoveryApi.getBaseUrl('auth-gateway');

    return `${gatewayUrl}/${AUTH_GATEWAY_ROUTE}`;
  }

  protected async put(url: string, body?: unknown): Promise<void> {
    const response = await this.fetchApi.fetch(url, {
      method: 'PUT',
      ...(body === undefined
        ? {}
        : {
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          }),
    });

    if (!response.ok) {
      throw await toRequestError(response);
    }
  }

  protected async post(url: string, body?: unknown): Promise<void> {
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      ...(body === undefined
        ? {}
        : {
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          }),
    });

    if (!response.ok) {
      throw await toRequestError(response);
    }
  }

  /**
   * Fetches a relative path (appended to the base url) and returns the
   * `data` array of the response envelope, filtered by the provided type
   * guard. Returns an empty array when the response does not contain an
   * array.
   */
  protected async fetchArray<T>(
    path: string,
    guard: (value: unknown) => value is T,
    init?: RequestInit,
  ): Promise<T[]> {
    const baseUrl = await this.getBaseUrl();

    const response = await this.fetchApi.fetch(
      `${baseUrl}${path}`,
      ...(init ? [init] : []),
    );

    if (!response.ok) {
      throw await toRequestError(response);
    }

    const body: DataResult<unknown[]> | null = await response.json();
    const data = body?.data;

    if (!Array.isArray(data)) {
      return [];
    }

    return data.filter(guard);
  }
}
